import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchHeartbeat, type HeartbeatData } from '../../lib/api/heartbeat';
import type { DaemonListResult } from '../../lib/api';
import { useRealtimeStatusStore, type RealtimeConnectionStatus } from '../../stores/realtimeStatusStore';
import { queryKeys } from './queryKeys';

// SSE가 정상일 때 heartbeat 폴링 주기. unreadCount/알림 즉시성은 SSE push가 담당하므로
// 폴링은 daemon lastSeenAt처럼 SSE로 옮기면 fan-out 폭주가 되는 저빈도 상태값을 위한
// "느린 pull"로만 남긴다(SSE로 이전하지 않는다).
export const SSE_CONNECTED_INTERVAL_MS = 120_000;
// SSE가 불안정(reconnecting/unstable)할 때의 폴백 주기. push를 신뢰할 수 없으므로 기존 주기로 회귀.
export const SSE_FALLBACK_INTERVAL_MS = 30_000;
// 대량 단절 시 전 클라이언트가 동일 시점에 폴백 폴링을 재개하는 thundering herd를 막기 위해
// 매 폴링 간격에 ±20% 지터를 섞어 요청을 시간축으로 분산한다.
export const POLL_JITTER_RATIO = 0.2;

// SSE 연결 상태에 따른 heartbeat 폴링 주기(지터 포함)를 계산한다. 훅 밖에서 단위 테스트하기
// 쉽도록 순수 함수로 분리했다.
export const computeHeartbeatRefetchIntervalMs = (status: RealtimeConnectionStatus): number => {
  const baseMs = status === 'connected' ? SSE_CONNECTED_INTERVAL_MS : SSE_FALLBACK_INTERVAL_MS;
  const jitter = baseMs * POLL_JITTER_RATIO * (Math.random() * 2 - 1);
  return Math.round(baseMs + jitter);
};

export const updateDaemonCacheFromHeartbeat = (
  cached: DaemonListResult | undefined,
  heartbeat: HeartbeatData,
): DaemonListResult | undefined => {
  if (!cached) return undefined;

  const heartbeatDaemonMap = new Map(heartbeat.daemons.map((daemon) => [daemon.id, daemon]));
  return {
    ...cached,
    daemons: cached.daemons.map((daemon) => {
      const heartbeatDaemon = heartbeatDaemonMap.get(daemon.id);
      return heartbeatDaemon
        ? { ...daemon, lastSeenAt: heartbeatDaemon.lastSeenAt, pendingVersion: heartbeatDaemon.pendingVersion }
        : daemon;
    }),
  };
};

export const useHeartbeat = (token: string | null, projectId: string, options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const realtimeStatus = useRealtimeStatusStore((state) => state.status);

  const query = useQuery<HeartbeatData>({
    queryKey: queryKeys.heartbeat.all(projectId),
    queryFn: () => fetchHeartbeat(token as string, projectId),
    enabled: Boolean(token) && projectId.length > 0 && (options?.enabled ?? true),
    // SSE 연결 상태에 따라 폴링 주기를 동적으로 전환한다. 정상이면 저빈도 pull,
    // 불안정하면 기존 주기로 폴백한다. refetchIntervalInBackground는 기본값(false)이라
    // 숨김 탭에서는 폴링이 멈춘다(visibility 게이팅은 react-query 기본 동작에 위임).
    refetchInterval: () => computeHeartbeatRefetchIntervalMs(realtimeStatus),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!query.data) return;

    // Distribute heartbeat data to individual query caches
    queryClient.setQueryData(queryKeys.notifications.unreadCount(projectId), query.data.unreadNotificationCount);

    // Update daemon lastSeenAt and pendingVersion in cache
    const cached = queryClient.getQueryData<DaemonListResult>(queryKeys.daemons.all);
    const updated = updateDaemonCacheFromHeartbeat(cached, query.data);
    if (updated) queryClient.setQueryData(queryKeys.daemons.all, updated);
  }, [query.data, queryClient, projectId]);

  return query;
};
